// The matcher's right pane: what each invoice still has outstanding, line by
// line. These are the targets an armed ERA line is applied to.
//
// ONE ERA line pays ONE invoice line: clicking a line applies the armed
// budget to it, capped at what it owes, and clicking a different line moves
// the selection there. The amount stays editable inline for the exceptions.
//
// An invoice whose earlier payments were not tracked per line cannot be paid
// per line at all (the server refuses it, and would double-count if it did not),
// so those render disabled with the reason on hover rather than failing after
// the user has done the work.

import React from "react";
import { Badge, Button, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { zar } from "../Accounts/lineAllocation";
import { targetKey } from "./useEraMatchModel";

const fmtDate = (raw) => {
  if (!raw) return "";
  // A bare YYYY-MM-DD (the shape line dates arrive in) is parsed by `new Date`
  // as UTC midnight, which a negative-offset zone would show as the day before.
  // Pinning it to local midnight keeps the calendar date the user entered.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00` : raw);
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString("en-GB");
};

export default function OutstandingLinesPane({
  invoices,
  armed,
  targets,
  suggestions,
  stagedKeys,
  readOnly = false,
  showPaid = false,
  onToggleShowPaid,
  onToggleTarget,
  onOpenInvoice,
  onNeedArm,
  onChangeLiability,
}) {
  // Paid invoices are history, not targets; they hide behind the toggle so
  // the pane reads as a worklist by default but can show the whole account.
  const paidCount = invoices.filter((inv) => inv.status === "Paid").length;
  const visibleInvoices = invoices.filter(
    (inv) => showPaid || inv.status !== "Paid"
  );
  const rowClass = (invoice, line) => {
    const key = targetKey(invoice._id, line.lineNumber);
    if (stagedKeys?.has(key)) return "era-match-row era-match-applied";
    if (targets[key]) return "era-match-row era-match-applied";
    if (suggestions[key]) return "era-match-row era-match-suggested";
    if (line.matchSource === "manual") return "era-match-row era-match-manual";
    if (line.matchSource === "auto") return "era-match-row era-match-auto";
    return "era-match-row";
  };

  // A settled line still accepts a zero-budget ("Not paid") ERA line: the
  // mapping is an acknowledgment and moves nothing.
  const isSelectable = (invoice, line) =>
    !readOnly &&
    !!armed &&
    invoice.canPayPerLine &&
    (line.medAidOutstanding > 0 || armed.budget === 0) &&
    !stagedKeys?.has(targetKey(invoice._id, line.lineNumber));

  const activate = (invoice, line) => (e) => {
    if (e.type === "keydown" && !["Enter", " "].includes(e.key)) return;
    if (e.type === "keydown") e.preventDefault();
    if (!isSelectable(invoice, line)) {
      // The commonest confusion on this screen was a dead click here with no
      // armed line. Point at the remittance pane instead of saying nothing.
      if (!readOnly && !armed && onNeedArm) onNeedArm();
      return;
    }
    // The row itself is the fly-animation source, so the selection visibly
    // leaves the place the user clicked.
    onToggleTarget(invoice, line, e.currentTarget);
  };

  // The applied amount is NOT editable: one ERA line pays one invoice line,
  // so the amount is always the scheme's payment capped at what the line
  // owes. Less is a shortfall decision, more is a surplus decision, both
  // asked in the workbench. The cell fills primary and a small x deselects.
  const appliedEditor = (invoice, line, key, target) => (
    <div className="d-flex align-items-center">
      <OverlayTrigger placement="top" overlay={<Tooltip>Remove from this match</Tooltip>}>
        <Button
          variant="link"
          className="text-white text-decoration-none p-0 ms-1 border-0"
          style={{ width: 18, height: 18, lineHeight: 1 }}
          aria-label={`Remove line ${line.tariffCode} from this match`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleTarget(invoice, line);
          }}
        >
          <i className="fa-solid fa-xmark" />
        </Button>
      </OverlayTrigger>
      <span
        className="flex-grow-1 text-end"
        style={{ padding: "0 0.25rem" }}
        aria-label={`Amount applied to line ${line.tariffCode}`}
      >
        {zar.format(target.appliedAmount)}
      </span>
    </div>
  );

  const shortfallOf = (invoice, line) => {
    const target = targets[targetKey(invoice._id, line.lineNumber)];
    if (!target) return 0;
    return Number((target.maxAmount - target.appliedAmount).toFixed(2));
  };

  return (
    <>
      <div className="era-match-pane-head">
        <h5 className="text-primary mb-0">Outstanding Invoices</h5>
        <div className="d-flex align-items-center gap-2">
          {armed && (
            <span className="small text-muted">
              Applying {armed.tariffCode || "the armed line"}
            </span>
          )}
          {paidCount > 0 && onToggleShowPaid && (
            <Form.Check
              type="switch"
              id="era-show-paid-invoices"
              label={`Show paid (${paidCount})`}
              checked={showPaid}
              onChange={onToggleShowPaid}
            />
          )}
        </div>
      </div>

      {visibleInvoices.length === 0 && (
        <p className="text-center py-4">
          {invoices.length > 0
            ? "Every invoice here is fully paid. Use the toggle above to show them."
            : "No outstanding invoices on this account."}
        </p>
      )}

      {visibleInvoices.map((invoice) => (
        <div key={invoice._id} className="mb-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
            <span className="fw-semibold">
              {[invoice.patientName, invoice.invoiceNumber]
                .filter(Boolean)
                .join(" - ")}
              <span className="small text-muted ms-2">
                {fmtDate(invoice.invoiceDate)}
              </span>
            </span>
            <span className="d-flex align-items-center gap-2">
              <Badge bg={invoice.status === "Paid" ? "success" : "secondary"}>
                {invoice.status}
              </Badge>
              {!invoice.canPayPerLine && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{invoice.perLineBlockedReason}</Tooltip>}
                >
                  <Badge bg="warning" text="dark">
                    <i className="fa-solid fa-triangle-exclamation me-1" />
                    Invoice level only
                  </Badge>
                </OverlayTrigger>
              )}
              {onOpenInvoice && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Open invoice detail</Tooltip>}>
                  <button
                    type="button"
                    className="btn btn-link text-primary text-decoration-none p-0 border-0"
                    style={{ width: 24, height: 24 }}
                    aria-label={`Open invoice ${invoice.invoiceNumber}`}
                    onClick={() => onOpenInvoice(invoice)}
                  >
                    <i className="fa-solid fa-circle-info" />
                  </button>
                </OverlayTrigger>
              )}
            </span>
          </div>

          <div className="d-none d-lg-block">
            <Table bordered hover responsive size="sm">
              <thead className="text-center">
                <tr>
                  <th className="text-center align-middle text-primary">Tariff</th>
                  <th className="text-center align-middle text-primary">Date</th>
                  <th className="text-center align-middle text-primary">Description</th>
                  <th
                    className="text-center align-middle text-primary acc-list-num"
                    style={{ width: 130 }}
                  >
                    Patient
                  </th>
                  <th
                    className="text-center align-middle text-primary"
                    style={{ width: 40 }}
                  >
                    <span className="visually-hidden">Change liability</span>
                  </th>
                  <th
                    className="text-center align-middle text-primary acc-list-num"
                    style={{ width: 130 }}
                  >
                    Medical Aid
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line) => {
                  const selectable = isSelectable(invoice, line);
                  const shortfall = shortfallOf(invoice, line);
                  return (
                    <tr
                      key={`${invoice._id}_${line.lineNumber}`}
                      data-era-row={targetKey(invoice._id, line.lineNumber)}
                      className={rowClass(invoice, line)}
                      role={selectable ? "button" : undefined}
                      tabIndex={selectable ? 0 : undefined}
                      aria-pressed={
                        selectable
                          ? !!targets[targetKey(invoice._id, line.lineNumber)]
                          : undefined
                      }
                      onClick={activate(invoice, line)}
                      onKeyDown={activate(invoice, line)}
                      style={
                        !invoice.canPayPerLine ? { opacity: 0.55 } : undefined
                      }
                    >
                      <td className="align-middle">{line.tariffCode || "-"}</td>
                      <td className="align-middle text-center text-nowrap">
                        {fmtDate(line.lineDate) || "-"}
                      </td>
                      <td className="align-middle text-nowrap">
                        {line.description ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{line.description}</Tooltip>}
                          >
                            <span className="era-match-desc">
                              {line.description}
                            </span>
                          </OverlayTrigger>
                        ) : (
                          "-"
                        )}
                        {suggestions[targetKey(invoice._id, line.lineNumber)] &&
                          !targets[targetKey(invoice._id, line.lineNumber)] && (
                            <Badge bg="light" text="dark" className="ms-2">
                              Suggested
                            </Badge>
                          )}
                        {shortfall > 0.005 && (
                          <span className="small text-muted ms-2">
                            Short {zar.format(shortfall)}
                          </span>
                        )}
                      </td>
                      <td
                        className="acc-list-num align-middle"
                        style={{ width: 130 }}
                      >
                        {zar.format(line.patientOutstanding)}
                      </td>
                      <td
                        className="text-center align-middle era-match-cell-plain"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {onChangeLiability && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                Change liability: move this invoice's amounts
                                between patient and medical aid
                              </Tooltip>
                            }
                          >
                            <button
                              type="button"
                              className="btn btn-link text-primary text-decoration-none p-0 border-0"
                              style={{ width: 24, height: 24 }}
                              aria-label={`Change liability on invoice ${invoice.invoiceNumber}`}
                              onClick={() => onChangeLiability(invoice)}
                            >
                              <i className="fa-solid fa-arrow-right-arrow-left" />
                            </button>
                          </OverlayTrigger>
                        )}
                      </td>
                      {(() => {
                        const key = targetKey(invoice._id, line.lineNumber);
                        const staged = stagedKeys?.get(key);
                        if (staged !== undefined) {
                          return (
                            <td
                              className="acc-list-num align-middle fw-semibold"
                              style={{ width: 130 }}
                            >
                              <i className="fa-solid fa-check me-2 text-primary" />
                              {zar.format(staged)}
                            </td>
                          );
                        }
                        const target = targets[key];
                        return target ? (
                          <td
                            className="acc-list-num align-middle bg-primary text-white p-0"
                            style={{ width: 130 }}
                          >
                            {appliedEditor(invoice, line, key, target)}
                          </td>
                        ) : (
                          <td className="acc-list-num align-middle" style={{ width: 130 }}>
                            {zar.format(line.medAidOutstanding)}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="d-lg-none">
            {invoice.lines.map((line) => {
              const selectable = isSelectable(invoice, line);
              const shortfall = shortfallOf(invoice, line);
              return (
                <div
                  key={`${invoice._id}_${line.lineNumber}`}
                  className={`acc-card era-match-card-line ${rowClass(invoice, line)}`}
                  role={selectable ? "button" : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  aria-pressed={
                    selectable
                      ? !!targets[targetKey(invoice._id, line.lineNumber)]
                      : undefined
                  }
                  onClick={activate(invoice, line)}
                  onKeyDown={activate(invoice, line)}
                  style={!invoice.canPayPerLine ? { opacity: 0.55 } : undefined}
                >
                  <div className="acc-card-top">
                    <span className="acc-card-id">{line.tariffCode || "Line"}</span>
                    <span className="acc-card-amount">
                      {zar.format(line.medAidOutstanding)}
                    </span>
                  </div>
                  <div className="acc-card-body">
                    <div className="acc-card-field">
                      <span className="acc-card-label">Date</span>
                      <span className="acc-card-value">{fmtDate(line.lineDate) || "-"}</span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Description</span>
                      <span className="acc-card-value">{line.description || "-"}</span>
                    </div>
                    {shortfall > 0.005 && (
                      <div className="acc-card-field">
                        <span className="acc-card-label">Shortfall</span>
                        <span className="acc-card-value">{zar.format(shortfall)}</span>
                      </div>
                    )}
                    <div className="acc-card-field">
                      <span className="acc-card-label">Patient</span>
                      <span className="acc-card-value">
                        {zar.format(line.patientOutstanding)}
                      </span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Medical Aid</span>
                      <span className="acc-card-value">
                        {(() => {
                          const key = targetKey(invoice._id, line.lineNumber);
                          const staged = stagedKeys?.get(key);
                          if (staged !== undefined) {
                            return (
                              <span className="fw-semibold">
                                <i className="fa-solid fa-check me-2 text-primary" />
                                {zar.format(staged)}
                              </span>
                            );
                          }
                          const target = targets[key];
                          return target ? (
                            <span className="d-inline-block bg-primary text-white rounded px-1" style={{ minWidth: 130 }}>
                              {appliedEditor(invoice, line, key, target)}
                            </span>
                          ) : (
                            zar.format(line.medAidOutstanding)
                          );
                        })()}
                      </span>
                    </div>
                    {onChangeLiability && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeLiability(invoice);
                        }}
                      >
                        <i className="fa-solid fa-arrow-right-arrow-left me-2" />
                        Change liability
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}
